import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { DEFAULT_TENANT_ROLE } from '@/features/auth/tenant-role';

interface TenantContextValue {
  selectedTenantId: string | null;
  setSelectedTenantId: (id: string) => void;
  selectedTenantTimezone: string;
  setSelectedTenantTimezone: (timezone: string) => void;
  isPlatformAdmin: boolean;
  isKombatixUser: boolean;
  tenantRole: string;
  hasMinRole: (required: string) => boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const isPlatformAdmin =
    user?.tokenRole === 'super_admin' || user?.tokenRole === 'kombatix_qa';

  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    user?.companyId ?? null,
  );
  const [selectedTenantTimezone, setSelectedTenantTimezone] = useState('America/New_York');

  useEffect(() => {
    if (!user?.companyId) {
      if (!isPlatformAdmin) setSelectedTenantId(null);
      return;
    }

    if (!isPlatformAdmin || !selectedTenantId) {
      setSelectedTenantId(user.companyId);
    }
  }, [user?.companyId, isPlatformAdmin, selectedTenantId]);

  // AuthContext already resolved this (claim -> users/{uid} -> mapping -> viewer,
  // refusing a disabled member). Re-deriving it here from the LEGACY values
  // re-introduced the exact bug the resolution fixes: `tenantToLegacyRole`
  // collapses manager -> tenant_admin, so reading tenant_admin back as 'admin'
  // promoted every manager, and `role === 'admin'` did the same off the legacy
  // Firestore field. Never infer a tenant role — take the resolved one or the floor.
  const tenantRole = user?.tenantRole ?? DEFAULT_TENANT_ROLE;

  const RANK: Record<string, number> = { admin: 4, manager: 3, support: 2, viewer: 1 };
  const hasMinRoleFn = (required: string) => {
    if (isPlatformAdmin) return true;
    return (RANK[tenantRole] ?? 0) >= (RANK[required] ?? 0);
  };

  return (
    <TenantContext.Provider
      value={{
        selectedTenantId,
        setSelectedTenantId,
        selectedTenantTimezone,
        setSelectedTenantTimezone,
        isPlatformAdmin,
        isKombatixUser: isPlatformAdmin,
        tenantRole,
        hasMinRole: hasMinRoleFn,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}
